import React from 'react';
import { Card, Typography, Radio, Tag } from 'antd';
import { BookableItem } from '../../../../../context/viator-product-types';
import { ProductDetail } from '../../../../../context/viator-product-types';
import { getAgeBandLabel } from '../label-helpers';
import { AgeBand } from '../../../../../context/dataExperienceContext';
import { getBookableItemPricingRecord, useTotalAmountForItem } from '../availability-and-price-helpers';
import { useCurrency } from '../../../../../context/currencyContext';
import CancellationPolicy from '../CancellationPolicy';
import useExperienceData from '../../../../../hooks/useExperienceData';
import { useTravelers } from '../../../../../context/travelersContext';

const { Title: AntTitle } = Typography;

interface PriceBreakdownProps {
  bookableItem: BookableItem;
}

export const PriceBreakdown: React.FC<PriceBreakdownProps> = ({ bookableItem }) => {
  const pricing = useTotalAmountForItem(bookableItem);
  const { experience } = useExperienceData();
  const promoterComission = experience?.price.promoter_commission || 0;
  const { convert } = useCurrency();
  if(!pricing) return null;
  const { totalAmount, productCurrency, totalBreakdown, countForComission } = pricing;
  const { value: convertedCurrencyValue, code } = convert(totalAmount, productCurrency)
  const finalValueWithComissions = convertedCurrencyValue + countForComission * promoterComission;

  return (
    <div>
      <h3>
        {finalValueWithComissions.toFixed(2)} {code}
      </h3>
      {totalBreakdown.map(({ ageBand, count, price }) => {
        const { value: convertedPrice, code } = convert(price, productCurrency);
        const finalPriceWithComission = convertedPrice + promoterComission;
        return (
          <div key={ageBand} className="traveler-line">
            {count} {getAgeBandLabel(ageBand as AgeBand)} x {finalPriceWithComission === 0 ? '- Gratis' : `${finalPriceWithComission.toFixed(2)} ${code}`}
          </div>
        );
      })}
    </div>
  );
};

interface BookableItemProps {
  item: BookableItem;
  selectedDate: string;
  productOptions: ProductDetail['productOptions'];
  isSelected: boolean;
  onSelect: (productOptionCode: string | null) => void;
  cancellationPolicy: ProductDetail['cancellationPolicy'];
  handleSubmit?: () => void;
}

const BookableItemComponent: React.FC<BookableItemProps> = ({
  item,
  selectedDate,
  productOptions,
  isSelected,
  onSelect,
  cancellationPolicy,
  handleSubmit
}) => {
  const { travelers } = useTravelers();
  const productOption = productOptions.find(option => option.productOptionCode === item.productOptionCode);
  const currentPricingRecord = getBookableItemPricingRecord(item, selectedDate, travelers);

  const operatingHours = currentPricingRecord?.timedEntries;

  const everyTimeEntryIsUnavialable = operatingHours?.every(operatingHour => operatingHour.unavailableDates?.find(unavailableDate => unavailableDate.date === selectedDate));
  const isUnavailable =  currentPricingRecord === undefined || everyTimeEntryIsUnavialable;

  return (
    <Card
      key={item.productOptionCode}
      className={`${isSelected ? 'selected' : ''}`}
      bordered={true}
      style={{
        borderColor: isSelected ? '#1890ff' : '#d9d9d9',
        borderWidth: '1px',
        borderStyle: 'solid',
        marginBottom: '16px',
        transition: 'border-color 0.3s'
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div style={{ flex: 2 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Radio checked={isSelected} disabled={isUnavailable} onChange={(e) => {
                e.preventDefault();
                if(!isUnavailable) {
                  onSelect(item.productOptionCode)
                }
              }} />
              <AntTitle level={4}>
                {productOption?.title || `Option ${item.productOptionCode}`}
              </AntTitle>
            </div>
            {productOption?.description && (
              <div
                dangerouslySetInnerHTML={{ __html: productOption.description }}
              />
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
            {operatingHours && operatingHours.map((operatingHour, index) => {
              const unavailableDate = operatingHour.unavailableDates?.find(unavailableDate => unavailableDate.date === selectedDate);
              if (unavailableDate) {
                return (
                  <Tag
                    key={index}
                    style={{ fontSize: '14px', padding: '4px 12px' }}
                  >
                    {`${operatingHour.startTime} Unavailable - ${unavailableDate.reason}`}
                  </Tag>
                );
              }
              return (
                <Tag
                  key={index}
                  color="blue"
                  style={{ fontSize: '14px', padding: '4px 12px' }}
                >
                  {operatingHour.startTime}
                </Tag>
              );
            })}
          </div>
          <div style={{ fontSize: '14px', paddingTop: '12px' }}>
            <CancellationPolicy cancellationPolicy={cancellationPolicy} />
          </div>



        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', borderLeft: '1px solid #e8e8e8' }}>
          {currentPricingRecord && (
            <div className="price-section" style={{ alignSelf: 'center' }}>
              <PriceBreakdown
                bookableItem={item}
              />
            </div>
          )}
          <div className="action-button" style={{ margin: '12px' }}>
            <button disabled={isUnavailable} onClick={(e) => {
              e.stopPropagation();
              if (handleSubmit && !isUnavailable) handleSubmit();
            }}>
              {isUnavailable ? "No disponible" : "Reservar ahora" }
            </button>
          </div>
        </div>
      </div>

    </Card>
  );
};

interface BookableItemsProps {
  items: BookableItem[];
  selectedDate: string;
  productOptions: ProductDetail['productOptions'];
  cancellationPolicy: ProductDetail['cancellationPolicy'];
  handleSubmit?: () => void;
}

const BookableItems: React.FC<BookableItemsProps> = ({
  items,
  selectedDate,
  productOptions,
  cancellationPolicy,
  handleSubmit
}) => {
  const { travelers, selectedItemCode, setSelectedItemCode} = useTravelers();

  const sortedBookableItems = items.sort((a, b) => {
    const isAvailableA = getBookableItemPricingRecord(a, selectedDate, travelers);
    const isAvailableB = getBookableItemPricingRecord(b, selectedDate, travelers);
    if (isAvailableA && !isAvailableB) return -1;
    if (!isAvailableA && isAvailableB) return 1;
    return 0;
  });

  return (
    <div style={{ width: '100%' }}>
      {sortedBookableItems.map((item) => (
        <BookableItemComponent
          key={item.productOptionCode}
          item={item}
          selectedDate={selectedDate}
          productOptions={productOptions}
          isSelected={selectedItemCode === item.productOptionCode}
          onSelect={setSelectedItemCode}
          cancellationPolicy={cancellationPolicy}
          handleSubmit={handleSubmit}
        />
      ))}
    </div>
  );
};

export default BookableItems;